<template>
  <div>
    <div class="input-title">Automatic Speech Recognition (ASR)</div>
    <div>
      <v-row>
        <v-col class="d-flex align-end" cols="12" sm="12" lg="12">
          <v-select
            v-model="asr.grammar_name"
            :items="grammars"
            item-text="name"
            item-value="name"
            label="Grammar"
            hide-details
            dense
            clearable
            clear-icon="fa-times"
            class="m-l-asr"
          />
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                v-bind="attrs"
                v-on="on"
                class="m-l-10"
                @click="goToGrammar"
              >
                fa-arrow-up-right-from-square
              </v-icon>
            </template>
            <span>Open Grammars</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="p-t-5 p-b-0" cols="12" sm="7" lg="7">
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <div class="m-l-asr" v-on="on">
                Timeout
              </div>
            </template>
            <span>
              Duration when no speech is detected and a noinput event is raised.
            </span>
          </v-tooltip>
        </v-col>
        <v-col class="d-flex p-t-5 p-b-0" cols="12" sm="5" lg="5">
          <div>
            <v-text-field
              class="margin0 padding0 width70px"
              v-model="asr.timeout"
              type="number"
              min="0"
              step="0.01"
              @blur="setPropertyData('timeout')"
              hide-details
              dense
              reverse
            ></v-text-field>
          </div>
          <div class="ml-2">seconds</div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="p-t-5 p-b-0" cols="12" sm="7" lg="7">
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <div class="m-l-asr" v-on="on">
                Complete timeout
              </div>
            </template>
            <span>
              Length of silence after speech, before a result is accepted. Lower
              value means bot will respond faster but speakers may get cut off
              during short pauses.<br /><br />
              Reasonable complete timeout values are typically in the range of
              0.3 seconds (highly responsive bot) to 2.0 seconds (tolerant to
              short pauses).
            </span>
          </v-tooltip>
        </v-col>
        <v-col class="d-flex p-t-5 p-b-0" cols="12" sm="5" lg="5">
          <div>
            <v-text-field
              class="margin0 padding0 width70px"
              v-model="asr.complete_timeout"
              type="number"
              min="0"
              step="0.01"
              @blur="setPropertyData('complete_timeout')"
              hide-details
              dense
              reverse
            ></v-text-field>
          </div>
          <div class="ml-2">seconds</div>
        </v-col>
      </v-row>
      <v-row v-if="isVxml">
        <v-col class="p-t-5 p-b-0" cols="12" sm="7" lg="7">
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <div class="m-l-asr" v-on="on">
                Incomplete timeout (VXML)
              </div>
            </template>
            <span>
              Length of silence after user speech, before a result is rejected
              with a nomatch event when the speech is an incomplete match of all
              active grammars.<br /><br />
              The incomplete timeout is usually longer than the complete timeout
              to allow users to pause mid-utterance (for example, to
              breathe).<br /><br />
              (Applicable only for VXML)
            </span>
          </v-tooltip>
        </v-col>
        <v-col class="d-flex p-t-5 p-b-0" cols="12" sm="5" lg="5">
          <div>
            <v-text-field
              class="margin0 padding0 width70px"
              v-model="asr.incomplete_timeout"
              type="number"
              min="0"
              step="0.01"
              @blur="setPropertyData('incomplete_timeout')"
              hide-details
              dense
              reverse
            ></v-text-field>
          </div>
          <div class="ml-2">seconds</div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="p-t-5 p-b-0" cols="12" sm="7" lg="7">
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <div class="m-l-asr" v-on="on">
                Max speech
              </div>
            </template>
            <span>
              Maximum duration of user speech.
            </span>
          </v-tooltip>
        </v-col>
        <v-col class="d-flex p-t-5 p-b-0" cols="12" sm="5" lg="5">
          <div>
            <v-text-field
              class="margin0 padding0 width70px"
              v-model="asr.max_speech_timeout"
              type="number"
              min="0"
              step="0.01"
              @blur="setPropertyData('max_speech_timeout')"
              hide-details
              dense
              reverse
            ></v-text-field>
          </div>
          <div class="ml-2">seconds</div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="p-t-5 p-b-0" cols="12" sm="7" lg="7">
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <div class="m-l-asr" v-on="on">
                ASR confidence threshold
              </div>
            </template>
            <span>
              Minimum confidence score. Recognizer rejects utterances with
              scores below this threshold with a nomatch event.<br /><br />
              Values range from 0.0 (least strict) to 1.0 (most strict).
            </span>
          </v-tooltip>
        </v-col>
        <v-col class="d-flex p-t-5 p-b-0" cols="12" sm="5" lg="5">
          <div>
            <v-text-field
              class="margin0 padding0 width70px"
              v-model="asr.asr_confidence_threshold"
              type="number"
              min="0"
              max="1"
              step="0.01"
              @blur="setPropertyData('asr_confidence_threshold')"
              hide-details
              dense
              reverse
            ></v-text-field>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="p-t-5 p-b-0">
          <div class="d-flex">
            <v-checkbox v-model="asr.voice_barge_in" hide-details></v-checkbox>
            <v-tooltip left :max-width="350">
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  @click="asr.voice_barge_in = !asr.voice_barge_in"
                >
                  Voice barge in
                </div>
              </template>
              <span>
                Allow user speech to interrupt before the prompt has finished
                playing.<br /><br />
                Applicable only when the previous component is Prompt.
              </span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>
      <v-row v-if="asr.voice_barge_in && isSip">
        <v-col
          class="m-l-asr align-center d-flex justify-start p-t-0 p-b-0"
          cols="12"
          sm="11"
          lg="11"
        >
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                Sensitivity
              </div>
            </template>
            <span>
              Barge-in sensitivity level. Higher sensitivity means lower voices
              or surrounding noises can easily trigger barge-in.
            </span>
          </v-tooltip>
          <v-slider
            v-model="asr.voice_barge_in_sensitivity"
            :min="1"
            :max="5"
            :step="1"
            hide-details
            class="m-l-10 slider-scroll"
          ></v-slider>
          <span class="slider-text m-l-10">
            {{
              asr.voice_barge_in_sensitivity
                ? setVoiceBarginThreshold(asr.voice_barge_in_sensitivity)
                : ''
            }}
          </span>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'

import apiGrammar from '@/apis/grammar'

export default {
  props: {
    defaultInput: Object,
    filterConfig: String
  },
  watch: {
    defaultInput: {
      deep: true,
      immediate: true,
      handler() {
        this.asr = this.defaultInput.asr
      }
    },
    'asr.grammar_name': {
      handler(val) {
        this.asr.grammar_id = ''
        if (val) {
          const grammar = _.find(this.grammars, { name: val })
          if (grammar) this.asr.grammar_id = grammar._id
        }
      }
    }
  },
  created() {
    this.getGrammars()
  },
  data() {
    return {
      asr: {},
      grammars: []
    }
  },
  computed: {
    isVxml() {
      return this.filterConfig === 'all' || this.filterConfig === 'vxml'
    },
    isSip() {
      return (
        this.filterConfig === 'all' ||
        this.filterConfig === 'true_voice_asr' ||
        this.filterConfig === 'google'
      )
    }
  },
  methods: {
    async getGrammars() {
      try {
        const { data } = await apiGrammar.getGrammar()
        this.grammars = data
      } catch {}
    },
    setPropertyData(property) {
      let val = this.asr[property]

      if (val < 0) {
        val = 0
      }

      if (property === 'asr_confidence_threshold' && val > 1) {
        val = 1
      }

      this.asr[property] = parseFloat(val).toFixed(2)
    },
    setVoiceBarginThreshold(value) {
      if (value == 1) {
        this.asr.voice_barge_in_threshold = 4096
        return 'Lowest'
      } else if (value == 2) {
        this.asr.voice_barge_in_threshold = 2048
        return 'Low'
      } else if (value == 3) {
        this.asr.voice_barge_in_threshold = 1024
        return 'Medium'
      } else if (value == 4) {
        this.asr.voice_barge_in_threshold = 512
        return 'High'
      } else if (value == 5) {
        this.asr.voice_barge_in_threshold = 256
        return 'Highest'
      }
    },
    goToGrammar() {
      const routeData = this.$router.resolve({
        path: '/settings/grammars'
      })
      window.open(routeData.href, '_blank')
    }
  }
}
</script>
<style scoped lang="scss">
.width70px {
  // text-align: right !important;
  width: 70px;
}
.m-l-asr {
  margin-left: 32px;
}
.slider-scroll {
  width: 240px;
  max-width: 240px;
}
.slider-text {
  width: 50px;
  // text-align: right;
}
</style>
